import React, { FC, useMemo } from 'react';
import UserPermissionGate from 'components/UserPermissionGate';
import { DropdownMenu, Icon, IconButton } from '@kontentino/ui';
import { useActions } from 'app/hooks/useActions';
import { faArrowDownSquareTriangle } from '@fortawesome/pro-regular-svg-icons/faArrowDownSquareTriangle';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faArrowTurnDownRight } from '@fortawesome/pro-regular-svg-icons/faArrowTurnDownRight';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowUpFromBracket';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical';
import MediaCountLabel from 'app/components/mediaAttachmentsDownload/components/MediaCountLabel';
import { useTranslation } from 'react-i18next';
import { PostDetail } from 'types/PostDetail';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { getPostAttachmentCount } from 'app/modules/posts/utils/getPostAttachmentCount';
import { useWidgetsSetup } from '../../layout/widgetsSetup/WidgetsSetupProvider';
import { getMediaDownloadSizes } from 'app/modules/posts/utils/getMediaDownloadSizes';
import { isLocalPostLocked } from 'app/modules/posts/utils/globalPosts';

type Props = {
  postId: PostDetail['id'];
};

const QuickActionsMoreDropdown: FC<Props> = ({ postId }) => {
  const { data } = useQuery<PostDetail>(queryKey.postDetail(postId), () =>
    PostApi.detail(postId),
  );
  const mediaDownloadSizes = data ? getMediaDownloadSizes(data) : [];
  const { t } = useTranslation();
  const { setSetupModalOpen, canSetupWidgets } = useWidgetsSetup();
  const { postActions } = useActions('post-detail');

  const hasAtLeastOneAttachment = useMemo(() => {
    const { videos, images } = getPostAttachmentCount(
      data?.attachments?.cards || [],
    );

    return videos > 0 || images > 0;
  }, [data?.attachments]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          icon={<Icon icon={faEllipsisVertical} />}
          data-name="posts-preview-more"
          data-cy="post-preview-more"
          variant="secondary"
          size="small"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="tw-w-60 tw-z-10">
        <UserPermissionGate scopes={postActions.shareExternally.scopes}>
          <DropdownMenu.Item
            onClick={() => postActions.shareExternally.perform({ id: postId })}
          >
            <Icon icon={faArrowUpFromBracket} /> {t('shareExternally')}
          </DropdownMenu.Item>
        </UserPermissionGate>
        <UserPermissionGate scopes={postActions.move.scopes}>
          <DropdownMenu.Item
            onClick={() => postActions.move.perform({ id: postId })}
          >
            <Icon icon={faArrowTurnDownRight} /> {t('move')}
          </DropdownMenu.Item>
        </UserPermissionGate>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger disabled={!hasAtLeastOneAttachment}>
            <DropdownMenu.Item
              data-name="posts-media-download"
              data-cy="posts-media-download"
            >
              <Icon icon={faArrowDownToLine} />
              {t('downloadPostAssets')}
              <Icon size="sm" className="tw-ml-auto" icon={faChevronRight} />
            </DropdownMenu.Item>
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="tw-z-[1000]">
              {data && <MediaCountLabel post={data} />}
              {mediaDownloadSizes.map((size, index) => (
                <DropdownMenu.Item
                  key={`download-options-${index}`}
                  onClick={() =>
                    postActions.downloadAssets.perform({
                      id: postId,
                      size: size.value,
                    })
                  }
                >
                  <Icon icon={faArrowDownToLine}></Icon>
                  {t(size.tKey)}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
        {canSetupWidgets && data && !isLocalPostLocked(data) && (
          <DropdownMenu.Item onClick={() => setSetupModalOpen.toggle()}>
            <Icon icon={faArrowDownSquareTriangle} />
            {t('widgetsSetup.modal.title')}
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default QuickActionsMoreDropdown;
